import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';

import { interval, of } from 'rxjs';
import {
  catchError,
  concatMap,
  exhaustMap,
  filter,
  map,
  mapTo,
  startWith,
  switchMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';

import { DeviceGroupSelectors } from 'src/app/features/device-group/store/selectors';
import { DocumentsActions } from 'src/app/features/documents';
import { EndorsementsActions } from 'src/app/features/endorsements';
import { GenericSpinnerComponent, ModalsActions } from 'src/app/features/modals';
import { NotificationsActions, NotificationType } from 'src/app/features/notifications';
import { ExceptionType } from 'src/app/features/notifications/models';
import { PackageUsersSelectors } from 'src/app/features/package-users';
import { PackagesSelectors, ProductType } from 'src/app/features/packages';
import { PresignActions } from 'src/app/features/pre-sign/store';
import { SignalRActions } from 'src/app/features/signal-r';
import { RootStoreState } from 'src/app/store';
import { v4 as uuid } from 'uuid';

import { SessionAddParticipantModalComponent, SigningCompletedComponent } from '../../components';
import { SigningCompletedModalComponent } from '../../components/signing-completed-modal';
import { SigningRoomService } from '../../services';
import { SigningRoomActions } from '../actions';
import { MultiFactorActions } from 'src/app/features/multi-factor/store/actions';
import { PinValidationActions } from 'src/app/features/pin-validation';
import { DeviceGroupActions } from 'src/app/features/device-group';
import { DeviceGroupService } from 'src/app/features/device-group/services';
import { SigningRoomSelectors } from '../selectors';
import { concatLatestFrom } from '@ngrx/operators';
import { ActionBlockingSnackbarComponent } from 'src/app/features/notifications/components';

@Injectable()
export class SigningRoomEffects {
  private readonly failedToGetAttemptCompletion = 'Failed to get attempt completion.';

  connectToSigningRoom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SigningRoomActions.AttemptSigningRoomConnection),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(this.store.pipe(select(PackageUsersSelectors.getActivePackageUserGuid)))
        )
      ),
      switchMap(([action, packageUserGuid]) => {
        return [
          SignalRActions.AttemptSignalRHubConnection({
            payload: { packageUserGuid },
          }),
        ];
      })
    )
  );

  $signingSessionStarted = createEffect(() =>
    this.actions$.pipe(
      ofType(SigningRoomActions.SigningSessionStarted),
      switchMap(() => [
        DocumentsActions.GetDocuments({
          payload: { ignoreIfLoaded: true, refetchSmartDocs: true },
        }),
        EndorsementsActions.FetchEndorsementImages(),
        ModalsActions.DisableGlobalSpinner(),
      ])
    )
  );

  cancelAttemptPreSignCompletion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SigningRoomActions.CancelAttemptPreSignCompletion),
      map(() => ModalsActions.HideLoadingSpinner())
    )
  );

  attemptPreSignCompletionPolling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SigningRoomActions.StartAttemptPreSignCompletion),
      exhaustMap(() =>
        interval(5000).pipe(
          startWith(0),
          mapTo(SigningRoomActions.AttemptPreSignCompletion()),
          takeUntil(this.cancelAttemptPreSignCompletion$)
        )
      )
    )
  );

  attemptPreSignCompletion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SigningRoomActions.AttemptPreSignCompletion),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(select(PackageUsersSelectors.getActivePackageUserGuid)),
            this.store.pipe(select(DeviceGroupSelectors.getDeviceCode))
          )
        )
      ),
      exhaustMap(([_, packageUserGuid, deviceCode]) =>
        this.signingRoomService.attemptCompletion(packageUserGuid, deviceCode).pipe(
          switchMap((payload: any) => {
            if (payload.isComplete) {
              return [
                SigningRoomActions.CancelAttemptPreSignCompletion(),
                SigningRoomActions.SigningSessionCompleted(),
                ModalsActions.SetModalComponent({
                  payload: {
                    component: SigningCompletedComponent,
                    modalTitle: 'Pre-Signing Completed',
                    allowManualClose: false,
                  },
                }),
              ];
            }

            return [ModalsActions.HideLoadingSpinner()];
          }),
          catchError((err) =>
            of(
              ModalsActions.ClearModalComponent(),
              NotificationsActions.AddNotification({
                payload: {
                  exception: err,
                  notificationType: NotificationType.Error,
                  id: uuid(),
                  text: this.failedToGetAttemptCompletion,
                  exceptionType: ExceptionType.CannotProceed,
                },
              })
            )
          )
        )
      )
    )
  );

  showSpinnerWhileAttemptingCompletion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SigningRoomActions.AttemptPreSignCompletion),
      map(() => ModalsActions.ShowLoadingSpinner())
    )
  );

  sessionJoined$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SigningRoomActions.SigningSessionJoined),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(select(PackageUsersSelectors.getActivePackageUserGuid)),
            this.store.pipe(select(DeviceGroupSelectors.getDeviceCode))
          )
        )
      ),
      switchMap(([_, packageUserGuid, deviceCode]) =>
        this.signingRoomService.sessionJoined(packageUserGuid, deviceCode).pipe(
          map(() => SigningRoomActions.SigningSessionJoinedSuccessful()),
          catchError((err) =>
            of(
              NotificationsActions.AddNotification({
                payload: {
                  exception: err,
                  notificationType: NotificationType.Error,
                  id: uuid(),
                  text: 'Failed to set active document',
                },
              })
            )
          )
        )
      )
    )
  );

  completeSigningRoomSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SigningRoomActions.CompleteSigningRoomSession),
      concatLatestFrom(() => [
        this.store.pipe(select(PackagesSelectors.getActivePackageGuid)),
        this.store.pipe(select(PackageUsersSelectors.getActivePackageUserGuid)),
        this.store.pipe(select(DeviceGroupSelectors.getDeviceCode)),
      ]),
      exhaustMap(([_, packageGuid, packageUserGuid, deviceCode]) =>
        this.signingRoomService
          .triggerSigningCompletion(packageGuid, packageUserGuid, deviceCode)
          .pipe(
            switchMap(() => {
              return [];
            }),
            catchError((err) =>
              of(
                NotificationsActions.AddNotification({
                  payload: {
                    exception: err,
                    notificationType: NotificationType.Error,
                    id: uuid(),
                    text: this.failedToGetAttemptCompletion,
                    exceptionType: ExceptionType.CannotProceed,
                  },
                })
              )
            )
          )
      )
    )
  );

  showSpinnerDuringSigningRoomPackageCompletion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SigningRoomActions.InitiateCompleteSigningRoomSession),
      map(() =>
        ModalsActions.SetStandaloneModalComponent({
          payload: { component: GenericSpinnerComponent },
        })
      )
    )
  );

  openAddUserToSessionModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SigningRoomActions.OpenAddUserToSessionModal),
      switchMap(() => [
        DeviceGroupActions.GetCurrentDeviceUsers(),
        ModalsActions.SetStandaloneModalComponent({
          payload: {
            component: SessionAddParticipantModalComponent,
          },
        }),
      ])
    )
  );

  promptUserForAuthentication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SigningRoomActions.PromptUserForAuthentication),
      concatMap((action) =>
        of(action).pipe(withLatestFrom(this.store.pipe(select(PackagesSelectors.getProductType))))
      ),
      switchMap(([action, productType]) => {
        const participant = action.payload.participant;
        if (participant.isMfaRequired && productType === ProductType.RemoteSigning) {
          return [
            ModalsActions.ClearModalComponent(),
            MultiFactorActions.StartMultiFactorChallengeFromModalComponent({
              payload: {
                packageUserGuid: participant.packageUserGuid,
              },
            }),
          ];
        }

        if (productType === ProductType.RemoteSigning) {
          return [
            ModalsActions.ClearModalComponent(),
            PinValidationActions.StartPinChallenge({
              payload: {
                participant: participant,
                onSuccessAction: null,
              },
            }),
          ];
        }
        return [];
      })
    )
  );

  continueAddUserToThisSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeviceGroupActions.AddParticipantToDeviceSuccessful),
      concatLatestFrom(() => [
        this.store.pipe(select(DeviceGroupSelectors.getDeviceCode)),
        this.store.pipe(select(SigningRoomSelectors.sessionJoined)),
      ]),
      switchMap(([action, deviceCode, isDeviceCodeInSigningRoom]) => {
        if (isDeviceCodeInSigningRoom) {
          return this.deviceGroupService
            .addUserToDevice(action.payload.packageUserGuid, deviceCode)
            .pipe(
              switchMap(() => [
                DeviceGroupActions.SetParticipantRejoining({
                  payload: {
                    isParticipantRejoining: true,
                    packageUserGuid: action.payload.packageUserGuid,
                  },
                }),
                DeviceGroupActions.GetCurrentDeviceUsers(),
              ]),
              catchError((err) =>
                of(
                  NotificationsActions.AddNotification({
                    payload: {
                      exception: err,
                      notificationType: NotificationType.Error,
                      id: uuid(),
                      text: 'Failed to add participant to session',
                      exceptionType: ExceptionType.CannotProceed,
                    },
                  })
                )
              )
            );
        }
        return [];
      })
    )
  );

  openSigningCompleteModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SigningRoomActions.OpenSigningCompletionModal),
      switchMap(() =>
        of(
          ModalsActions.SetStandaloneModalComponent({
            payload: {
              component: SigningCompletedModalComponent,
              shouldFade: true,
            },
          })
        )
      )
    )
  );

  getSigningStartedStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SigningRoomActions.GetSigningStartedStatus),
      switchMap((action) => {
        return this.signingRoomService.getSigningStarted().pipe(
          map((signingStartedResponse) => {
            return SigningRoomActions.SetSigningStartedStatus({
              payload: { hasSigningStarted: signingStartedResponse.hasSigningStarted },
            });
          }),
          catchError((err) => {
            return of(
              NotificationsActions.AddNotification({
                payload: {
                  exception: err,
                  notificationType: NotificationType.Error,
                  id: uuid(),
                  text: 'Failed to get signing started status',
                  exceptionType: ExceptionType.ReloadRetry,
                },
              })
            );
          })
        );
      })
    )
  );

  sendDisruptionNotificationToSigningAgent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SigningRoomActions.SendDisruptionNotificationToSigningAgent),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(this.store.pipe(select(PackageUsersSelectors.getIsSigningAgent)))
        )
      ),
      filter(([action, isSigningAgent]) => isSigningAgent),
      switchMap(([action]) => {
        return of(
          NotificationsActions.AddSnackBarNotification({
            payload: {
              component: ActionBlockingSnackbarComponent,
              title: action.payload.title,
              body: action.payload.message,
              panelClass: 'mdc-snackbar-error',
              duration: 12000,
            },
          })
        );
      })
    )
  );

  configureListeners$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SignalRActions.SignalRRoomJoined, PresignActions.PreSignConfigureListeners),
        map(() => {
          this.signingRoomService.configureListeners();
        })
      ),
    { dispatch: false }
  );

  constructor(
    private readonly actions$: Actions,
    private readonly deviceGroupService: DeviceGroupService,
    private readonly signingRoomService: SigningRoomService,
    private readonly store: Store<RootStoreState.State>
  ) {}
}
